import styled from "@emotion/styled"
import { PButtonTile, PIcon, PLinkTile, PTag, PText } from "@porsche-design-system/components-react"
import {
	breakpoint,
	colorCanvas,
	colorContrastLow,
	colorFrosted,
	durationMd,
	easeInOut,
	getFocusVisibleStyle,
	radius3Xl,
	spacingFluidMd,
	spacingStaticLg,
	spacingStaticMd,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import type { MouseEvent, HTMLAttributeAnchorTarget } from "react"
import { useCallback } from "react"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { navigationMessages, NewBadge } from "src/components"
import imageConfigurator from "./link-tile-configurator.jpg"
import imageInventory from "./link-tile-inventory.jpg"

const messages = defineMessages({
	buildsheetsLinkDescription: {
		defaultMessage: "Look up build sheets for Porsche vehicles.",
		id: "home.buildsheetsLink.description",
	},
	comingSoon: {
		defaultMessage: "Coming soon",
		id: "home.comingSoon",
	},
	configuratorLinkDescription: {
		defaultMessage: "Create and edit customer configurations in Dealer Mode.",
		id: "home.configuratorLink.description",
	},
	configuratorLinkDescriptionComingSoon: {
		defaultMessage:
			"Create and edit customer configurations in Dealer Mode. This feature will be available in your market soon. Further rollout details will follow.",
		id: "home.configuratorLinkComingSoon.description",
	},
	inventoryLinkDescription: {
		defaultMessage: "Manage vehicles – from listing to delivery.",
		id: "home.inventoryLink.description",
	},
	pccsLinkDescription: {
		defaultMessage: "Check service, warranty and vehicle status by VIN.",
		id: "home.pccsLink.description",
	},
	vehicleInformationLinkDescription: {
		defaultMessage: "Find Build Sheets, Monroney Labels and additional vehicle information by VIN.",
		id: "home.vehicleInformationLink.description",
	},
})

type LinkTarget = "inventoryList" | "configurator" | "buildsheets" | "vehicleInformation" | "pcss" | "pcw" | "pcwCms"

export interface HomeLinksGridProps {
	hrefs: Partial<Record<LinkTarget, string>>
	onLinkClick?: (target: LinkTarget, e: MouseEvent) => void
}

export const HomeLinksGrid = ({ hrefs, onLinkClick }: HomeLinksGridProps) => {
	const { formatMessage } = useIntl()

	const onClick = useCallback(
		(target: LinkTarget) => (e: MouseEvent) => {
			onLinkClick?.(target, e)
		},
		[onLinkClick],
	)

	return (
		<TileLayout>
			{hrefs.inventoryList && (
				<PLinkTile
					className="scheme-dark"
					label={formatMessage(navigationMessages.inventoryList)}
					description={formatMessage(navigationMessages.inventoryList)}
					href={hrefs.inventoryList}
					aspectRatio={{ base: "4/3", s: "1/1" }}
					compact
					gradient
					onClick={onClick("inventoryList")}
				>
					<Image src={imageInventory} alt={formatMessage(navigationMessages.inventoryList)} />
					<PText slot="footer" className="scheme-dark">
						<FormattedMessage {...messages.inventoryLinkDescription} />
					</PText>
				</PLinkTile>
			)}

			{hrefs.configurator ? (
				<PLinkTile
					className="scheme-dark"
					label={formatMessage(navigationMessages.configuratorDealerMode)}
					description={formatMessage(navigationMessages.configuratorDealerMode)}
					href={hrefs.configurator}
					aspectRatio={{ base: "4/3", s: "1/1" }}
					compact
					gradient
					onClick={onClick("configurator")}
				>
					<Image src={imageConfigurator} alt={formatMessage(navigationMessages.configuratorDealerMode)} />
					<PText slot="footer" className="scheme-dark">
						<FormattedMessage {...messages.configuratorLinkDescription} />
					</PText>
				</PLinkTile>
			) : (
				<PButtonTile
					label={formatMessage(navigationMessages.configuratorDealerMode)}
					description={formatMessage(navigationMessages.configuratorDealerMode)}
					aspectRatio={{ base: "4/3", s: "1/1" }}
					disabled
					compact
				>
					<PTag slot="header">
						<FormattedMessage {...messages.comingSoon} />
					</PTag>
					<ImageBlurred
						src={imageConfigurator}
						alt={formatMessage(navigationMessages.configuratorDealerMode)}
					/>
					<PText slot="footer" className="scheme-dark">
						<FormattedMessage {...messages.configuratorLinkDescriptionComingSoon} />
					</PText>
				</PButtonTile>
			)}

			<SubTileLayout>
				{hrefs.buildsheets && (
					<WhiteLinkTile
						label={formatMessage(navigationMessages.buildsheets)}
						description={formatMessage(messages.buildsheetsLinkDescription)}
						href={hrefs.buildsheets}
						onClick={onClick("buildsheets")}
					/>
				)}
				{hrefs.vehicleInformation && (
					<WhiteLinkTile
						label={formatMessage(navigationMessages.vehicleInformation)}
						description={formatMessage(messages.vehicleInformationLinkDescription)}
						href={hrefs.vehicleInformation}
						onClick={onClick("vehicleInformation")}
					/>
				)}
				{hrefs.pcss && (
					<WhiteLinkTile
						label={formatMessage(navigationMessages.pcss)}
						description={formatMessage(messages.pccsLinkDescription)}
						href={hrefs.pcss}
						target="_blank"
						onClick={onClick("pcss")}
					/>
				)}
				{hrefs.pcw && (
					<WhiteLinkTile
						label="Porsche Center Website"
						description="See how your Porsche Center appears to customers."
						href={hrefs.pcw}
						target="_blank"
						onClick={onClick("pcw")}
					/>
				)}
				{hrefs.pcwCms && (
					<WhiteLinkTile
						label="Porsche Center Website CMS"
						description="Edit and publish content on your Porsche Center website."
						href={hrefs.pcwCms}
						target="_blank"
						onClick={onClick("pcwCms")}
					/>
				)}
			</SubTileLayout>
		</TileLayout>
	)
}

const TileLayout = styled.section`
    display: grid;
    gap: ${spacingStaticMd};

    @media (min-width: ${breakpoint.s}px) {
        grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(0, 0.65fr);
        gap: ${spacingStaticLg};
    }
`

const SubTileLayout = styled.div`
    display: grid;
    gap: ${spacingStaticMd};
    grid-auto-rows: minmax(0, 1fr);
`

const Image = styled.img`
    background: ${colorContrastLow};
`

const ImageBlurred = styled(Image)`
	filter: blur(5px) grayscale(1) brightness(1.2);
`

interface WhiteLinkTileProps {
	description: string
	href?: string
	isNew?: boolean
	label: string
	onClick?: (e: MouseEvent) => void
	target?: HTMLAttributeAnchorTarget
}
const WhiteLinkTile = ({ description, isNew, label, target, ...rest }: WhiteLinkTileProps) => {
	const labelText = (
		<LabelText size="md" weight="semibold">
			{label}
		</LabelText>
	)

	return (
		<TileLink target={target} {...rest}>
			<TileLinkFooter>
				{isNew ? (
					<LabelNewBadgeWrapper>
						{labelText}
						<NewBadge compact />
					</LabelNewBadgeWrapper>
				) : (
					labelText
				)}
				<TileLinkDescription>{description}</TileLinkDescription>
				<TileLinkIcon name={target === "_blank" ? "external" : "arrow-right"} />
			</TileLinkFooter>
		</TileLink>
	)
}

// custom solution for white tiles on right side is necessary since PLinkTile always has a fixed aspect ratio,
// but we need it dynamic since there can be 1, 2 or 3 small tiles while the square ones on the left set the available height
// https://github.com/porsche-design-system/porsche-design-system/blob/main/packages/components/src/components/link-tile/link-tile-styles.ts#L128
const TileLink = styled.a`
    display: grid;
    grid-template: ${spacingFluidMd} auto minmax(0px, 1fr) auto ${spacingFluidMd} / ${spacingFluidMd} minmax(0px, 1fr) ${spacingFluidMd};
    position: relative;
    background: ${colorCanvas};
    ${getFocusVisibleStyle()};
	border-radius: ${radius3Xl};
    overflow: hidden;
    text-decoration: none;

    @media (hover: hover) and (pointer: fine) {
        transition: background-color ${durationMd} ${easeInOut};
        &:hover {
            background: ${colorFrosted};
        }
    }
`

const TileLinkFooter = styled.div`
    grid-area: 4 / 2;
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: ${spacingStaticMd};
`

const LabelText = styled(PText)`
	grid-column: 1 / -1;
`

const LabelNewBadgeWrapper = styled.div`
	display: flex;
	gap: ${spacingStaticSm};
	align-items: center;
	grid-column: 1 / -1;
`

const TileLinkDescription = styled(PText)`
	grid-row: 2;
`

const TileLinkIcon = styled(PIcon)`
	align-self: center;
	grid-column: 2;
	grid-row: 2;
`
